import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import axios from "../../api/axios";
import { formatDataGeneral } from "../../utils/utils";
import { Bar } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from "chart.js";
import APRComment from "./APRComment.js/AprComments";
import APRmemo from "./APRComment.js/APRmemo";

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const Annex4a = forwardRef(({ year, district, period, hideTableDis }, ref) => {
  const [tableData, setTableData] = useState([]);
  const currentYear = year ? year : new Date().getFullYear(); // 2025
  const years = [currentYear - 3, currentYear - 2, currentYear - 1, currentYear];

  useEffect(() => {
    getData();
  }, [year, district, period]);

  useImperativeHandle(ref, () => ({
    getData: () => ({
      indicator: "Table_9",
      tableData
    }),
  }));

  const mapRevenueDataOld = (data, report) => {
    const revenueNames = [
      "GOG", "IGF", "Donor"
    ];

    const attributes = data[0]?.attributes;
    const reports = report.find(rep => rep.trackedEntity === data[0]?.trackedEntity);

    const result = revenueNames.map(revName => {
      const estimates = {};
      const performance = {};
      years.forEach(year => {
        const baselineItem = attributes.find(item =>
          item?.displayName?.toLowerCase() === `${revName.toLowerCase()} baseline ${year}`
        );
        const targetItem = attributes.find(item =>
          item?.displayName?.toLowerCase() === `${revName.toLowerCase()} target ${year}`
        );
        estimates[year] = baselineItem ? Number(baselineItem.value) : 0;
        performance[year] = targetItem ? Number(targetItem.value) : 0;
      });

      return {
        name: revName,
        estimates: estimates,
        performance: performance,
        actual: {}
      };
    });

    result.forEach(el => {
      for (let r of reports?.dataValues || []) {
        if (el.name === 'IGF' && r.dataElement === "Wp7KcuZgrJa") {
          const yearMatch = r.eventDate ? new Date(r.eventDate).getFullYear() : null;
          if (yearMatch && years.includes(yearMatch)) el.actual[yearMatch] = Number(r.value) || 0;
        } else if (el.name === 'GOG' && r.dataElement === "nHtXhtCsha8") {
          const yearMatch = r.eventDate ? new Date(r.eventDate).getFullYear() : null;
          if (yearMatch && years.includes(yearMatch)) el.actual[yearMatch] = Number(r.value) || 0;
        } else if (el.name === 'Donor' && r.dataElement === "jii2lhnqFfE") {
          const yearMatch = r.eventDate ? new Date(r.eventDate).getFullYear() : null;
          if (yearMatch && years.includes(yearMatch)) el.actual[yearMatch] = Number(r.value) || 0;
        }
      }
    });

    return result;
  };

  const mapData = (data, report) => {
    const names = [
      "GOG Salaries", "IGF", "Other Donors"
    ];

    const attributes = data[0]?.attributes;
    const reports = report.find(rep => rep.trackedEntity === data[0]?.trackedEntity);

    const result = names.map(name => {
      const estimateItem = attributes.find(item =>
        item?.displayName?.toLowerCase() === `${name.toLowerCase()} baseline`
      );

      return {
        name: name,
        estimate: estimateItem ? Number(estimateItem.value) : 0,
        performance: 0
      };
    });

    result.forEach(el => {
      for (let r of reports.dataValues) {
        if (el.name === 'GOG Salaries' && r.dataElement === "nHtXhtCsha8") {
          el.performance = Number(r.value);
          break;
        } else if (el.name === 'IGF' && r.dataElement === "Wp7KcuZgrJa") {
          el.performance = Number(r.value);
          break;
        } else if (el.name === 'Other Donors' && r.dataElement === "jii2lhnqFfE") {
          el.performance = Number(r.value);
          break;
        }
      }
    });

    return result;
  };

  function transformRevenu(data) {
    const keys = [
      "revenuThreeLessMapped",
      "revenuTwoLessMapped",
      "revenuOneLessMapped",
      "revenuFirstMapped"
    ];

    const suffixes = ["One", "Two", "Three", "Four"];

    // Get all names from the first set
    const names = data[keys[0]].map(item => item.name);

    // Transform grouped data
    const result = names.map(name => {
      let obj = { name };

      keys.forEach((key, i) => {
        const match = data[key].find(item => item.name === name);
        if (match) {
          obj[`estimate${suffixes[i]}`] = match.estimate;
          obj[`performance${suffixes[i]}`] = match.performance;
        }
      });

      return obj;
    });

    // Build totals row
    const totalRow = { name: "Total" };

    suffixes.forEach((suf, i) => {
      totalRow[`estimate${suf}`] = result.reduce(
        (sum, el) => sum + (el[`estimate${suf}`] || 0),
        0
      );
      totalRow[`performance${suf}`] = result.reduce(
        (sum, el) => sum + (el[`performance${suf}`] || 0),
        0
      );
    });

    result.push(totalRow);
    return result;
  }


  function getData() {
    const startDate = `${years[0]}-01-01`;
    const endDate = `${years[3]}-12-31`;

    axios
      .get(`/tracker/trackedEntities?orgUnit=${district}&program=SY8TpfPgzr9&startDate=${startDate}&endDate=${endDate}`)
      .then(result => {
        if (result.data.instances.length > 0) {
          axios
            .get(`/tracker/events?program=SY8TpfPgzr9&orgUnit=${district}&startDate=${startDate}&endDate=${endDate}`)
            .then(resp => {

              const revenues = result.data.instances;
              const reports = resp.data.instances;

              const revenuFirst = formatDataGeneral(revenues, "Years", `${year}`) || [];
              const revenuOneLess = formatDataGeneral(revenues, "Years", `${year - 1}`) || [];
              const revenuTwoLess = formatDataGeneral(revenues, "Years", `${year - 2}`) || [];
              const revenuThreeLess = formatDataGeneral(revenues, "Years", `${year - 3}`) || [];

              const revenuFirstMapped = mapData(revenuFirst, reports);
              const revenuOneLessMapped = mapData(revenuOneLess, reports);
              const revenuTwoLessMapped = mapData(revenuTwoLess, reports);
              const revenuThreeLessMapped = mapData(revenuThreeLess, reports);
              const tempResult = { revenuFirstMapped,  revenuOneLessMapped, revenuTwoLessMapped, revenuThreeLessMapped };

              const formatedData = transformRevenu(tempResult);
              setTableData(formatedData);
            })
            .catch(err => console.log(err));
        }
      })
      .catch(err => console.log(err));
  }

  const chartData = {
    labels: tableData.map(item => item.name),
    datasets: [
      // {
      //   label: `Baseline ${years[0]} (GH¢)`,
      //   data: tableData.map(item => item.estimates[years[0]]),
      //   backgroundColor: "#1e8fff8e",
      //   borderColor: "#1e8fff8e",
      //   borderWidth: 1,
      // },
      // {
      //   label: `Actual ${years[3]} (GH¢)`,
      //   data: tableData.map(item => item.actual[years[3]] || 0),
      //   backgroundColor: "#f4080886",
      //   borderColor: "#ff000086",
      //   borderWidth: 1,
      // },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: `${years[0]} Baseline and ${years[3]} Actual Revenue Receipts`,
      },
    },
    scales: {
      y: {
        title: {
          display: true,
          text: "Revenue (GH¢)",
        },
        beginAtZero: true,
      },
    },
  };

  return (
    <div className="col-12">
      <h3>Annex 4a: Update On Revenue Sources and Expenditure</h3>
      <div className="card">
        <div className="card-header"></div>
        <div className="card-body">
          {/* <APRmemo
            year={year}
            districtId={district}
            tableCommentedId={`table9-${year}`}
            hideTableDis={hideTableDis}
          /> */}
          <div className="table-responsive">


            <table
              className="table table-bordered"
              style={{
                marginTop: 20,
                border: '1px solid #000',
                borderCollapse: 'collapse',
                width: '100%',
              }}
            >
              <thead
                style={{
                  backgroundColor: '#d4edda',
                  fontWeight: 'bold',
                }}
              >
                <tr>
                  <th style={{ border: '1px solid #000' }} rowSpan="2">Revenue Source</th>
                  <th style={{ border: '1px solid #000' }} colSpan="4">Estimates</th>
                  <th style={{ border: '1px solid #000' }} colSpan="4">Performance</th>
                </tr>
                <tr>
                  {years.map(year => (
                    <th key={year} style={{ border: '1px solid #000' }}>{year}</th>
                  ))}
                  {years.map(year => (
                    <th key={year} style={{ border: '1px solid #000' }}>{year}</th>
                  ))}
                </tr>
              </thead>
              {tableData && (
                <tbody>
                  {tableData.map((row, index) => (
                    <tr key={index}>
                    <td style={{ border: "1px solid #000" }}>{row.name !== 'GOG Salaries' ? row.name : 'GOG'}</td>

                    <td style={{ border: "1px solid #000" }}>
                      {row.estimateOne.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    
                    <td style={{ border: "1px solid #000" }}>
                      {row.performanceOne.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td style={{ border: "1px solid #000" }}>
                      {row.estimateTwo.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    
                    <td style={{ border: "1px solid #000" }}>
                      {row.performanceTwo.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td style={{ border: "1px solid #000" }}>
                      {row.estimateThree.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    
                    <td style={{ border: "1px solid #000" }}>
                      {row.performanceThree.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td style={{ border: "1px solid #000" }}>
                      {row.estimateFour.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                   
                    <td style={{ border: "1px solid #000" }}>
                      {row.performanceFour.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>


                  </tr>
                  ))}
                  
                </tbody>
              )}
            </table>
          </div>

          <h6>
            Figure 2.1 further shows the revenue trends of {years[0]} baseline and actual receipts for {years[3]}. It can be realized that the major source of funding for implementation of projects during the period remained the IGF which includes receipts from mineral revenue.
          </h6>

          <Bar data={chartData} options={chartOptions} />

          <p className="mt-2">
            <small>Source: MPCU</small>
          </p>

          <APRComment
            data={tableData}
            year={year}
            districtId={district}
            tableCommentedId={`table9-${year}`}
          >
            {({ renderCommentInput, renderCommentList }) => (
              <>
                {renderCommentInput()}
                {renderCommentList()}
              </>
            )}
          </APRComment>
        </div>
      </div>
    </div>
  );
});

export default Annex4a;